/**
 * Organization Detection Utility
 * 
 * Detects the organization/tenant from the current subdomain
 * Supports multi-tenancy with subdomain-based routing
 */

/**
 * Get the current organization from the subdomain
 * @returns {string} Organization identifier (e.g., 'alliedoms', 'development', 'default')
 */
export const getCurrentOrganization = () => {
  // Get the hostname (e.g., 'alliedoms.benchmarkoms.com' or 'localhost:3000')
  const hostname = window.location.hostname;
  
  // Extract subdomain
  const parts = hostname.split('.');
  
  // Handle localhost/development
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.includes('localhost')) {
    // Check localStorage for dev override, or default to 'development' for development
    const devOrg = localStorage.getItem('dev_organization');
    return devOrg || 'development';
  }
  
  // Handle production subdomains
  // Format: subdomain.benchmarkoms.com
  if (parts.length >= 3 && parts[parts.length - 2] === 'benchmarkoms' && parts[parts.length - 1] === 'com') {
    const subdomain = parts[0];
    
    // Validate allowed organizations
    const allowedOrganizations = ['alliedoms', 'development'];
    
    if (allowedOrganizations.includes(subdomain)) {
      return subdomain;
    }
    
    // Default to 'development' if subdomain is not recognized
    console.warn(`Unknown subdomain: ${subdomain}. Defaulting to 'development'`);
    return 'development';
  }
  
  // Handle Firebase hosting domains (e.g., benchmarkoms.web.app)
  if (hostname.includes('benchmarkoms.web.app') || hostname.includes('benchmarkoms.firebaseapp.com')) {
    // Check if there's a subdomain
    const subdomain = parts[0];
    if (subdomain && subdomain !== 'benchmarkoms') {
      return subdomain;
    }
    // Default to 'development' for main Firebase domain
    return 'development';
  }
  
  // Default fallback
  console.warn(`Could not determine organization from hostname: ${hostname}. Defaulting to 'development'`);
  return 'development';
};

/**
 * Get organization display name
 * @param {string} orgId - Organization identifier
 * @returns {string} Display name
 */
export const getOrganizationDisplayName = (orgId) => {
  const names = {
    'alliedoms': 'AlliedOMS',
    'development': 'Staging',
    'default': 'Default'
  };
  return names[orgId] || orgId;
};

/**
 * Validate organization identifier
 * @param {string} orgId - Organization identifier to validate
 * @returns {boolean} True if valid
 */
export const isValidOrganization = (orgId) => {
  const allowedOrganizations = ['alliedoms', 'development'];
  return allowedOrganizations.includes(orgId);
};

/**
 * Set development organization override (for localhost testing)
 * @param {string} orgId - Organization identifier
 */
export const setDevOrganization = (orgId) => {
  if (isValidOrganization(orgId)) {
    localStorage.setItem('dev_organization', orgId);
  } else {
    console.error(`Invalid organization: ${orgId}`);
  }
};

/**
 * Clear development organization override
 */
export const clearDevOrganization = () => {
  localStorage.removeItem('dev_organization');
};

/**
 * Known practice names that belong to the development organization.
 * Used to infer organization for legacy documents missing the organization field.
 */
const DEVELOPMENT_PRACTICES = [
  'Development',
  'development',
  'Dev',
  'dev',
  'Test',
  'test',
  'Staging',
  'staging',
  'Demo',
  'demo'
];

/**
 * Infer organization from a document's data.
 * Checks explicit organization field first, then infers from practice name for legacy data.
 * 
 * @param {Object} doc - Document data with optional organization and practice fields
 * @returns {string} Organization identifier ('alliedoms' or 'development')
 */
export const inferOrganization = (doc) => {
  if (!doc || typeof doc !== 'object') {
    return 'alliedoms';
  }
  
  // If organization field is explicitly set, use it
  if (doc.organization) {
    return doc.organization;
  }
  
  // Infer from practice name for legacy documents
  const practice = doc.practice || '';
  if (DEVELOPMENT_PRACTICES.some(devPractice => 
    practice.toLowerCase().includes(devPractice.toLowerCase())
  )) {
    return 'development';
  }
  
  // Default to alliedoms for legacy production data
  return 'alliedoms';
};

/**
 * Check if a document belongs to the specified organization.
 * Uses smart inference for legacy documents without organization field.
 * 
 * @param {Object} doc - Document data
 * @param {string} targetOrg - Target organization to match
 * @returns {boolean} True if document belongs to target organization
 */
export const matchesOrganization = (doc, targetOrg) => {
  return inferOrganization(doc) === targetOrg;
};
